import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { BarList, EmptyState, type BarListItem } from '@vnetwork/ui/data';

/**
 * Shared building blocks for the Manticore "Analytics page" archetype
 * (WAF, Bot Management, …). All helpers bind to the REAL, vendor-opaque
 * analytics responses (a `Record<string, unknown>` from the fan-out) — they
 * normalise defensively and never fabricate data.
 */

/**
 * Normalise a breakdown — an array of rows OR an object map — into ranked
 * `BarList` items (desc, top `max`). Tries the common label/count field names
 * seen across vendors (rule_id, country_code, ip, user_agent, …).
 */
export function asBars(v: unknown, opts: { labelKeys?: string[]; max?: number } = {}): BarListItem[] {
  const { labelKeys, max = 8 } = opts;
  const rows: Record<string, unknown>[] = Array.isArray(v)
    ? (v as Record<string, unknown>[])
    : v && typeof v === 'object'
      ? Object.entries(v as Record<string, unknown>).map(([name, value]) => ({ name, count: value }))
      : [];
  return rows
    .map((r) => {
      // Caller can pin the label column (e.g. client_ip for an IP list) — else
      // try the common label fields seen across vendors.
      const label = labelKeys
        ? String(labelKeys.map((k) => r[k]).find((x) => x != null && x !== '') ?? '—')
        : String(
            r.name ??
              r.rule_name ??
              r.rule_id ??
              r.category ??
              r.country ??
              r.country_code ??
              r.user_agent ??
              r.path ??
              r.ip ??
              r.client_ip ??
              '—',
          );
      const value = Number(r.count ?? r.value ?? r.requests ?? r.events ?? r.blocked ?? 0);
      return { label, value, display: value.toLocaleString() } satisfies BarListItem;
    })
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, max);
}

const DEFAULT_TIME_KEYS = ['timeline', 'timeseries', 'series', 'histogram', 'buckets', 'by_time', 'over_time'];

/**
 * Pull a time-series out of an analytics response IF one is present. Returns
 * `Record<string, unknown>[]` ready to hand straight to `<Chart>` — each point
 * keeps its original fields plus a formatted `t` (x label) and a numeric `v`
 * (total). Returns `[]` when the response carries no series (→ omit the chart).
 */
export function findTimeSeries(
  data: Record<string, unknown> | null | undefined,
  keys: string[] = DEFAULT_TIME_KEYS,
): Record<string, unknown>[] {
  if (!data) return [];
  for (const k of keys) {
    const arr = data[k];
    if (Array.isArray(arr) && arr.length > 0) {
      return (arr as Record<string, unknown>[]).map((p, i) => {
        const raw = p.time ?? p.timestamp ?? p.bucket ?? p.ts ?? p.date ?? i;
        const v = Number(p.count ?? p.value ?? p.total ?? p.requests ?? p.events ?? 0);
        return { ...p, t: fmtBucket(raw), v };
      });
    }
  }
  return [];
}

/** Format an epoch / ISO bucket to a short `HH:00` label; pass through otherwise. */
export function fmtBucket(raw: unknown): string {
  if (typeof raw === 'number' && raw > 1e9) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? String(raw) : `${String(d.getHours()).padStart(2, '0')}:00`;
  }
  if (typeof raw === 'string' && /\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return `${String(d.getHours()).padStart(2, '0')}:00`;
  }
  return String(raw);
}

/** Ranked "top-N" card — the analytics staple. */
export function BarCard({ title, items }: { title: string; items: BarListItem[] }) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {items.length === 0 ? (
          <EmptyState size="sm" title="No data" />
        ) : (
          <BarList items={items} />
        )}
      </CardBody>
    </Card>
  );
}
